import type {LicenseInfo} from '@github-ui/code-view-sidebar/types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RepoHeaderActions} from '@github-ui/repo-header-actions'
import {RepoMobileDetails} from '@github-ui/repo-header-actions/RepoMobileDetails'
import {useMemo} from 'react'
import {RepoSubtitle, RepoTitle} from './components/RepoTitle'
import {useBranchAndTagCount} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'
import type {HeaderResponse} from './types'
import {deriveRepoPaths} from './utils/repo-paths'
import {AdvisoryWorkspaceHeader} from './components/AdvisoryWorkspaceHeader'
import styles from './CodeViewHeader.module.css'
import {useErrorMessage} from './utils/use-error-message'

/** Data extracted from overviewFiles for the mobile about section. */
export interface OverviewFilesData {
  license?: LicenseInfo
  hasReadme?: boolean
  hasCodeOfConduct?: boolean
  hasContributing?: boolean
  hasSecurityPolicy?: boolean
  hasAccessibility?: boolean
}

/** Repository overview page header component for the code view. */
export function CodeViewHeader({
  ownerLogin,
  repoName,
  viewer,
  repo,
  advisoryInfo,
  showDemoNotification,
  hasAccessToCodespaces,
  hasCodeOfConduct,
  hasContributing,
  hasSecurityPolicy,
  hasAccessibility,
  ...rest
}: HeaderResponse & OverviewFilesData & {hasAccessToCodespaces?: boolean}) {
  const paths = useMemo(() => deriveRepoPaths(ownerLogin, repoName), [ownerLogin, repoName])
  const showError = useErrorMessage()
  const branchCount = useBranchAndTagCount()?.branches

  if (!viewer || (!viewer.isLoggedIn && !isFeatureEnabled('react_logged_out_repository_header'))) {
    return null
  }

  return (
    <>
      {repo.isAdvisoryWorkspace && <AdvisoryWorkspaceHeader repo={repo} advisoryInfo={advisoryInfo} />}
      <div className="tmp-pt-3 tmp-pb-3 border-bottom color-border-muted">
        <div className="hide-whenNarrow">
          <div className="d-none d-md-flex flex-wrap flex-items-center gapx-3 gapy-1">
            <div className={styles.TitleWrapper}>
              <RepoTitle
                repo={repo}
                ownerLogin={ownerLogin}
                repoName={repoName}
                paths={paths}
                isOrgOwned={rest.pin.isOrgOwned}
                showDemoNotification={showDemoNotification}
              />
            </div>
            {!repo.isAdvisoryWorkspace && (
              <div>
                <RepoHeaderActions
                  {...rest}
                  ownerLogin={ownerLogin}
                  repoName={repoName}
                  viewer={viewer}
                  repo={repo}
                  paths={paths}
                  hasAccessToCodespaces={hasAccessToCodespaces}
                  onError={showError}
                />
              </div>
            )}
          </div>
          <RepoSubtitle repo={repo} advisoryInfo={advisoryInfo} />
        </div>
        {!repo.isAdvisoryWorkspace && (
          <div className="show-whenNarrow">
            <RepoMobileDetails
              ownerLogin={ownerLogin}
              repoName={repoName}
              {...rest}
              repo={repo}
              viewer={viewer}
              paths={paths}
              hasAccessToCodespaces={hasAccessToCodespaces}
              branchCount={branchCount}
              onError={showError}
              hasCodeOfConduct={hasCodeOfConduct}
              hasContributing={hasContributing}
              hasSecurityPolicy={hasSecurityPolicy}
              hasAccessibility={hasAccessibility}
            />
          </div>
        )}
      </div>
    </>
  )
}
